import classNames from 'classnames'
import { memo, useMemo } from 'react'

import { ReactWidgetContext } from 'core-components/sitebuilder/layout/context/ReactWidgetContext'
import { WidgetsCreator } from 'core-components/sitebuilder/layout/WidgetsCreator'
import { IStaticProps, TStaticPropsValue } from 'core-components/sitebuilder/models/IWidget'

import { useWidgetMetaData } from '../hooks/useWidgetMetaData'
import { IWidgetContent } from '../models/IWidgetContent'
import { prepareStaticParamsTypes } from '../widgetItem/helpers/prepareStaticParamsTypes'

import styles from './Content.module.scss'

export const WidgetContent = memo(
  ({
    renderType,
    widget,
    pageWidget,
    widgetParams = {},
    routeParams,
    onResize,
    allowAutoHeight,
    widgetWidth,
    onChangeCustomSettings,
    onToggleColumnWidth,
    metrics,
    fullWidth,
    systemWidgetSettings,
    isFullScreen,
    onCloseFullScreen,
    onOpenFullScreen,
    renderCustomWidget,
    style = {},
  }: IWidgetContent) => {
    const { meta } = useWidgetMetaData({ widgetId: widget.i })
    const isReact = renderType === 'react'

    const stylesZoom =
      widget.scale !== 100
        ? {
            width: `${98 / (widget.scale / 100)}%`,
            minHeight: `${(widget.h * (widget.h > 40 ? 10.5 : 10) - 52) / (widget.scale / 100)}px`,
            transform: `scale(${widget.scale / 100})`,
            transformOrigin: '0px 0px',
          }
        : {}

    const rootStyle = { ...stylesZoom, ...style }

    const staticParams = useMemo(() => {
      const params: Record<string, string | number | boolean | object> = {}
      const staticPropsMapping = meta?.staticPropsMapping || []

      staticPropsMapping.forEach((element) => {
        const name = element.propName
        const existValue = pageWidget?.staticProps?.find((el) => el.paramName === name)
        const newValue: IStaticProps = {
          paramName: name,
          paramType: element.type,
          paramValue: element?.defaultValue as TStaticPropsValue,
        }
        params[name] = prepareStaticParamsTypes(existValue || newValue)
      })
      return params
    }, [pageWidget?.staticProps, meta])

    return (
      <div
        className={classNames(styles.root)}
        style={rootStyle}
        data-widget-name={widget.viewModel || widget.i}
      >
        {isReact && (
          <ReactWidgetContext.Provider
            value={{
              widgetId: widget.viewModel || widget.i,
              widgetParams,
              widgetWidth: widget.w,
              widgetFlexWidth: widgetWidth,
              widgetFullWidth: fullWidth,
              widgetCustomSettings: systemWidgetSettings?.customSettings || {},
              widgetIsFullScreen: isFullScreen,
            }}
          >
            <WidgetsCreator
              viewModel={widget.viewModel || widget.i}
              widgetParams={widgetParams}
              widgetWidth={widget.w}
              flexWidth={widgetWidth}
              staticParams={staticParams}
              routeParams={routeParams || {}}
              metrics={metrics}
              onChangeCustomSettings={onChangeCustomSettings}
              onToggleColumnWidth={onToggleColumnWidth}
              fullWidth={fullWidth}
              customSettings={systemWidgetSettings?.customSettings || {}}
              isFullScreen={isFullScreen}
              onCloseFullScreen={onCloseFullScreen}
              onOpenFullScreen={onOpenFullScreen}
            />
          </ReactWidgetContext.Provider>
        )}

        {!isReact && renderCustomWidget
          ? renderCustomWidget({
              widget,
              widgetParams,
              widgetWidth,
              allowAutoHeight: allowAutoHeight(widget),
              onResize,
              metrics,
            })
          : null}
      </div>
    )
  }
)

WidgetContent.displayName = 'WidgetContent'
